// screens/Lists.tsx — the Personal tab's Lists sub-tab (spec `spec-the-
// cockpit-personal-tab-and-the-01b-nav-build-1-2026-10-08` §2d, plate 02).
//
// List chips (title · open count); the open list's items, then "Done this
// week"; tick to done and un-tick; add items one per line; remove; a new
// list (family or private); archive. Every row says WHO by name — who added
// it, who ticked it — because a shared list is a conversation between the
// people on it, and an anonymous tick is a question nobody can answer.
//
// The lists come from the cockpit's own tenant AND the family tenant read
// beside it (ruling N3), one chip row; each list remembers which tenant it
// lives in so every write goes back to the same place. A new list is written
// to the family tenant when there is one (Personal.tsx's header says why),
// with private/family as its visibility.
//
// Core does the rules (lists/lists.ts): re-adding a done item un-dones it,
// re-adding an open one is "already there", private lists are the RLS
// policy's job. The receipts below say which of those happened, by name.
import { useCallback, useEffect, useState } from 'preact/hooks';
import {
  addListItems,
  archiveList,
  createList,
  getList,
  listLists,
  removeListItem,
  setListItemDone,
  type FamilyVisibility,
  type ListApi,
  type ListItemApi,
  type ListWithItemsApi,
} from '../lib/api';
import type { PersonalContext } from './Personal';
import { PersonalSheet, failureOf, whoLabel } from './personalShared';
import './Lists.css';

export interface ListsProps {
  tenantId: string;
  ctx: PersonalContext;
  /** The list open, from the URL; the first list when absent. */
  listId?: string;
  onSelectList: (listId: string) => void;
}

type Origin = 'mine' | 'family';
type ListChip = ListApi & { origin: Origin; tenantId: string };

type Load =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; lists: ListChip[]; familyError: string | null };

export function Lists({ tenantId, ctx, listId, onSelectList }: ListsProps) {
  const family = ctx.family;
  const [load, setLoad] = useState<Load>({ phase: 'loading' });
  const [detail, setDetail] = useState<ListWithItemsApi | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [newList, setNewList] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const closeNewList = useCallback(() => setNewList(false), []);

  useEffect(() => {
    let cancelled = false;
    const familyRead = family
      ? listLists(family.tenantId).then(
          (ls) => ({ lists: ls }),
          (err: unknown) => ({ error: failureOf(err) })
        )
      : Promise.resolve(null);
    Promise.all([listLists(tenantId), familyRead])
      .then(([mine, fam]) => {
        if (cancelled) return;
        const lists: ListChip[] = [
          ...(fam && 'lists' in fam ? fam.lists.map((l) => ({ ...l, origin: 'family' as const, tenantId: family!.tenantId })) : []),
          ...mine.map((l) => ({ ...l, origin: 'mine' as const, tenantId })),
        ];
        setLoad({ phase: 'ready', lists, familyError: fam && 'error' in fam ? fam.error : null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoad({ phase: 'error', message: failureOf(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, family?.tenantId, reloadKey]);

  const lists = load.phase === 'ready' ? load.lists : [];
  const selected = lists.find((l) => l.id === listId) ?? lists[0] ?? null;

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailError(null);
    getList(selected.tenantId, selected.id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) setDetailError(failureOf(err));
      });
    return () => {
      cancelled = true;
    };
  }, [selected?.id, selected?.tenantId, reloadKey]);

  function refresh(line: string | null) {
    if (line) setReceipt(line);
    setReloadKey((k) => k + 1);
  }

  if (load.phase === 'loading') {
    return (
      <div class="lists">
        <div class="lists__skeleton" aria-hidden="true" />
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div class="lists">
        <div class="personal__failure">The lists could not be read — {load.message}</div>
      </div>
    );
  }

  const writeTenant = family?.tenantId ?? tenantId;
  const badgeOf = (l: ListChip): string =>
    l.visibility === 'private' ? 'Private' : l.origin === 'family' || !family ? 'Family' : 'Mine';

  async function toggle(item: ListItemApi) {
    if (!selected) return;
    setBusy(item.id);
    try {
      const saved = await setListItemDone(selected.tenantId, selected.id, item.id, !item.done);
      refresh(saved.done ? `Ticked “${saved.text}”.` : `“${saved.text}” is back on ${selected.title}.`);
    } catch (err) {
      setReceipt(`Not changed — ${failureOf(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function remove(item: ListItemApi) {
    if (!selected) return;
    setBusy(item.id);
    try {
      await removeListItem(selected.tenantId, selected.id, item.id);
      refresh(`Took “${item.text}” off ${selected.title}.`);
    } catch (err) {
      setReceipt(`Not removed — ${failureOf(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function addItems(e: Event) {
    e.preventDefault();
    if (!selected || busy) return;
    const texts = draft
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);
    if (texts.length === 0) return;
    setBusy('add');
    try {
      const r = await addListItems(selected.tenantId, selected.id, texts);
      const added = r.results.filter((x) => x.outcome === 'added').map((x) => x.item.text);
      const undone = r.results.filter((x) => x.outcome === 'undone').map((x) => x.item.text);
      const already = r.results.filter((x) => x.outcome === 'already_open').map((x) => x.item.text);
      const parts: string[] = [];
      if (added.length) parts.push(`Added to ${selected.title}: ${added.join(', ')}.`);
      if (undone.length) parts.push(`Back on the list: ${undone.join(', ')}.`);
      if (already.length) parts.push(`Already there: ${already.join(', ')}.`);
      setDraft('');
      setAdding(false);
      refresh(parts.join(' '));
    } catch (err) {
      setReceipt(`Not added — ${failureOf(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function archive() {
    if (!selected) return;
    setBusy('archive');
    try {
      await archiveList(selected.tenantId, selected.id);
      setConfirmArchive(false);
      refresh(`Archived ${selected.title}. Nothing on it was deleted.`);
    } catch (err) {
      setReceipt(`Not archived — ${failureOf(err)}`);
    } finally {
      setBusy(null);
    }
  }

  const items = detail && selected && detail.id === selected.id ? detail.items : [];
  const open = items.filter((i) => !i.done);
  const done = items.filter((i) => i.done);

  return (
    <div class="lists-shell">
      <div class="lists">
        {selected ? (
          <div class="lists__head">
            <h1 class="lists__title">{selected.title}</h1>
            <span class={`lists__badge${selected.origin === 'family' && selected.visibility === 'family' ? ' is-family' : ''}`}>
              {badgeOf(selected)}
            </span>
          </div>
        ) : (
          <div class="lists__head">
            <h1 class="lists__title">Lists</h1>
          </div>
        )}

        <div class="lists__chips" role="list">
          {lists.map((l) => (
            <a
              key={`${l.tenantId}:${l.id}`}
              role="listitem"
              class={`lists__chip${selected && l.id === selected.id ? ' is-active' : ''}${l.origin === 'family' ? ' is-family' : ''}`}
              href={`#/personal/lists/${l.id}`}
              aria-current={selected && l.id === selected.id ? 'page' : undefined}
              onClick={(e) => {
                e.preventDefault();
                setReceipt(null);
                setConfirmArchive(false);
                onSelectList(l.id);
              }}
            >
              {l.title} · {l.openCount ?? 0}
            </a>
          ))}
          <button type="button" class="lists__chip is-new" onClick={() => setNewList(true)}>
            + New list
          </button>
        </div>

        {load.familyError ? (
          <div class="personal__failure">The family’s lists could not be read — {load.familyError}</div>
        ) : null}
        {receipt ? (
          <div class="personal__receipt" role="status">
            {receipt}
          </div>
        ) : null}

        {!selected ? <div class="personal__empty">No lists yet.</div> : null}
        {selected && detailError ? (
          <div class="personal__failure">{selected.title} could not be read — {detailError}</div>
        ) : null}

        {selected && !detailError && detail?.id === selected.id ? (
          <>
            {open.length === 0 ? (
              <div class="personal__empty">Nothing on {selected.title}.</div>
            ) : (
              <ul class="lists__items">
                {open.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    who={whoLabel(item.createdByUserId, item.createdVia, ctx.names, ctx.meId)}
                    busy={busy === item.id}
                    onToggle={() => toggle(item)}
                    onRemove={() => remove(item)}
                  />
                ))}
              </ul>
            )}
            {done.length > 0 ? (
              <>
                <h2 class="personal__section">Done this week</h2>
                <ul class="lists__items">
                  {done.map((item) => {
                    const by = whoLabel(item.doneByUserId, null, ctx.names, ctx.meId);
                    return (
                      <ItemRow
                        key={item.id}
                        item={item}
                        who={by ? `ticked by ${by}` : null}
                        busy={busy === item.id}
                        onToggle={() => toggle(item)}
                        onRemove={() => remove(item)}
                      />
                    );
                  })}
                </ul>
              </>
            ) : null}
            {confirmArchive ? (
              <div class="psheet__confirm lists__archive-confirm">
                <span>Archive {selected.title}? It leaves the chips; nothing on it is deleted.</span>
                <div class="psheet__confirm-row">
                  <button type="button" class="psheet__danger" disabled={busy === 'archive'} onClick={archive}>
                    Archive
                  </button>
                  <button type="button" class="personal__secondary" onClick={() => setConfirmArchive(false)}>
                    Keep it
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" class="lists__archive" onClick={() => setConfirmArchive(true)}>
                Archive {selected.title}
              </button>
            )}
          </>
        ) : null}
      </div>

      {selected ? (
        adding ? (
          <form class="lists__add" onSubmit={addItems}>
            <textarea
              class="lists__add-text"
              rows={3}
              value={draft}
              aria-label={`Items for ${selected.title}, one per line`}
              onInput={(e) => setDraft(e.currentTarget.value)}
            />
            <div class="lists__add-row">
              <button type="submit" class="personal__primary" disabled={busy === 'add' || draft.trim() === ''}>
                Add
              </button>
              <button
                type="button"
                class="personal__secondary"
                onClick={() => {
                  setAdding(false);
                  setDraft('');
                }}
              >
                Close
              </button>
            </div>
          </form>
        ) : (
          <div class="personal__actions">
            <button type="button" class="personal__primary" onClick={() => setAdding(true)}>
              Add items
            </button>
          </div>
        )
      ) : null}

      {newList ? (
        <NewListSheet
          writeTenant={writeTenant}
          hasFamily={family !== null}
          onClose={closeNewList}
          onCreated={(list) => {
            setNewList(false);
            setReceipt(`Started ${list.title}${list.visibility === 'private' ? ', only you see it' : ''}.`);
            setReloadKey((k) => k + 1);
            onSelectList(list.id);
          }}
        />
      ) : null}
    </div>
  );
}

function ItemRow({
  item,
  who,
  busy,
  onToggle,
  onRemove,
}: {
  item: ListItemApi;
  who: string | null;
  busy: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const id = `li-${item.id}`;
  return (
    <li class={`lists__item${item.done ? ' is-done' : ''}`}>
      <input id={id} type="checkbox" class="lists__tick" checked={item.done} disabled={busy} onChange={onToggle} />
      <label for={id} class="lists__text">
        {item.text}
      </label>
      {who ? <span class="lists__who">{who}</span> : null}
      <button type="button" class="lists__remove" disabled={busy} aria-label={`Remove ${item.text}`} onClick={onRemove}>
        ×
      </button>
    </li>
  );
}

function NewListSheet({
  writeTenant,
  hasFamily,
  onClose,
  onCreated,
}: {
  writeTenant: string;
  hasFamily: boolean;
  onClose: () => void;
  onCreated: (list: ListApi) => void;
}) {
  const [title, setTitle] = useState('');
  const [visibility, setVisibility] = useState<FamilyVisibility>('family');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function save(e: Event) {
    e.preventDefault();
    const name = title.trim();
    if (!name || busy) return;
    setBusy(true);
    setNote(null);
    try {
      onCreated(await createList(writeTenant, { title: name, visibility }));
    } catch (err) {
      setNote(`Not started — ${failureOf(err)}`);
      setBusy(false);
    }
  }

  return (
    <PersonalSheet title="New list" onClose={onClose}>
      <form class="psheet__form" onSubmit={save}>
        <label class="psheet__field">
          <span>Name</span>
          <input type="text" value={title} maxLength={200} onInput={(e) => setTitle(e.currentTarget.value)} />
        </label>
        <fieldset class="psheet__seg">
          <legend>Who sees it</legend>
          <label class={visibility === 'family' ? 'is-on' : ''}>
            <input
              type="radio"
              name="lvis"
              value="family"
              checked={visibility === 'family'}
              onChange={() => setVisibility('family')}
            />
            {hasFamily ? 'Family' : 'Everyone here'}
          </label>
          <label class={visibility === 'private' ? 'is-on' : ''}>
            <input
              type="radio"
              name="lvis"
              value="private"
              checked={visibility === 'private'}
              onChange={() => setVisibility('private')}
            />
            Only me
          </label>
        </fieldset>
        {note ? (
          <div class="psheet__note" role="alert">
            {note}
          </div>
        ) : null}
        <button type="submit" class="personal__primary" disabled={busy || title.trim() === ''}>
          Start the list
        </button>
      </form>
    </PersonalSheet>
  );
}
